import { type OpenIntranetChatResponse } from '@gb-ticket/shared-types';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { ApiRequestError } from '@/lib/api';
import { INTRANET_CHAT_PROFILE_UNAVAILABLE } from '@/products/intranet/features/intranet/intranet-chat-action';
import { useOpenIntranetChat } from '@/products/intranet/features/intranet/use-intranet-chat';
import { intranetChatPath } from '@/products/intranet/intranet-paths';

/**
 * Opens Teams in the same tab or navigates to the portal thread.
 * Does not open a new tab — mobile Universal Links need the current browsing context.
 */
export function useIntranetChatAction() {
  const navigate = useNavigate();
  const openChat = useOpenIntranetChat();

  async function startChat(userId: string): Promise<void> {
    try {
      const result = await openChat.mutateAsync(userId);
      applyOpenChatResult(result, navigate);
    } catch (error) {
      if (error instanceof ApiRequestError && error.error.statusCode === 404) {
        toast.error(INTRANET_CHAT_PROFILE_UNAVAILABLE);
        return;
      }
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Der Chat konnte nicht geöffnet werden.',
      );
    }
  }

  return { startChat, isPending: openChat.isPending };
}

/** Pure routing side of open — exported for tests. */
export function applyOpenChatResult(
  result: OpenIntranetChatResponse,
  navigate: (to: string) => void,
  assignLocation: (url: string) => void = (url) => {
    window.location.href = url;
  },
): void {
  if (result.mode === 'teams') {
    assignLocation(result.teamsUrl);
    return;
  }
  navigate(intranetChatPath(result.conversationId));
}
